/**
 * The read itself (SYR-111 phase 3).
 *
 * Takes the payload as a prop and renders nothing else, so the customer page
 * and the admin preview are the SAME render path rather than two that drift.
 * The admin preview passes `readOnly`, which makes the Action Plan toggles
 * inert: a preview must not commit plays to a brand's plan.
 */

import React, { useState } from 'react';
import { Card as AntCard, Space, Typography } from 'antd';
import { THEME } from '../../config/theme';
import { Card, Eyebrow } from '../../atoms';
import { ReadHero } from './ReadHero';
import { IModelBeliefsGateSummary } from '../../../../services/nestjs-base-api';
import { GateSummary } from './GateSummary';
import { OverallScores } from './OverallScores';
import { CompetitorScores } from './CompetitorScores';
import { CategoryScores } from './CategoryScores';
import { MemoryVsSearch } from './MemoryVsSearch';
import { FactGrid } from './FactGrid';
import { OpinionBeliefs } from './OpinionBeliefs';
import { SurveyOnly } from './SurveyOnly';
import { HowWeAsked } from './HowWeAsked';
import { RecordFixes, RecordFixEntry } from './RecordFixes';
import { SourcesPanel } from './SourcesPanel';
import { AskThemCard } from './AskThemCard';
import { ReadSection } from './ReadSection';
import { PagesThatMoveRank } from './PagesThatMoveRank';
import { WhatPagesSay } from './WhatPagesSay';
import { NeverNamedPages } from './NeverNamedPages';
import { AllTheData } from './AllTheData';
import { useIntl } from 'react-intl';
// The admin Brands tab and this page show the same chart: a read's history is
// the same object on both surfaces, and one of them drifting would be a lie.
import { ModelBeliefsHistoryChart } from '../../../../admin/components/model-beliefs/ModelBeliefsHistoryChart';

const { Text, Paragraph } = Typography;

interface Props {
  latest: any;
  // Points from the read, so the customer page needs no admin endpoint.
  history?: { date: string; aiBrandEquity: number | null; gateInclusionRate: number | null; models: number; panelSize: number }[];
  brandName: string;
  onToggleRecordFix?: (entry: RecordFixEntry) => void;
  busyPlayId?: string | null;
  // A preview renders everything and commits nothing.
  readOnly?: boolean;
  // Where "Send the survey" goes on a read with no customer line. Absent in
  // the admin preview, which shows the card without the button.
  onSendSurvey?: () => void;
}

export const ModelBeliefsRead: React.FunctionComponent<Props> = ({
  latest,
  history,
  brandName,
  onToggleRecordFix,
  busyPlayId,
  readOnly,
  onSendSurvey,
}) => {
  const intl = useIntl();
  // "All of the data" starts closed everywhere: the admin preview must show
  // exactly what the brand sees, so it opens the same way, by the button.
  const [allOpen, setAllOpen] = useState(false);
  if (!latest) return null;

  // The v3 read is statement-level: a run that carries statements renders the
  // three layers (rank, facts against competitors, the brand's own beliefs)
  // and no composite score. A run from before the groups existed keeps the
  // element view, so the history is still readable.
  const statements = (latest.statements ?? []) as any[];
  const v3 = statements.some((s) => s.group === 'A' || s.group === 'B');
  const hasSurvey = statements.some((s) => s.survey);

  const gate = latest.gate ?? null;
  const searched = (gate?.byMode ?? []).find((m: any) => m.retrievalMode === 'on');
  const memoryMode = (gate?.byMode ?? []).find((m: any) => m.retrievalMode === 'off');
  // The API ranks the table (the brand, its competitors, and any brand the
  // models put ahead of it that nobody configured). The fallback is for reads
  // served before the table existed.
  const heroOrder = gate?.rankTable
    ? (gate.rankTable as NonNullable<IModelBeliefsGateSummary['rankTable']>).map((row) => ({
        name: row.isBrand ? brandName : row.name,
        rankScore: row.rankScore,
        namedFirstRate: row.namedFirstRate,
        inclusionRate: row.inclusionRate,
        meanPosition: row.meanPosition,
        isBrand: row.isBrand,
        configured: row.configured,
      }))
    : gate && searched
    ? [
        {
          name: brandName,
          rankScore: searched.rankScore ?? null,
          namedFirstRate: searched.namedFirstRate ?? null,
          inclusionRate: searched.inclusionRate ?? null,
          meanPosition: searched.meanPosition ?? null,
          isBrand: true,
        },
        ...((gate.competitors ?? []) as any[]).map((c) => ({
          name: c.name,
          rankScore: c.rankScore ?? null,
          namedFirstRate: c.namedFirstRate ?? null,
          inclusionRate: c.inclusionRate ?? null,
          meanPosition: c.meanPosition ?? null,
          isBrand: false,
        })),
      ].sort((a, b) => (b.rankScore ?? 0) - (a.rankScore ?? 0))
    : [];
  // v4: the pages the models read. A section of its own, second, because the
  // pages are what move the rank: after whether you are recommended and before
  // what the models believe. Every later section moves down one.
  const pagesMode = latest.searchedSource === 'pages';
  const pagesRead: number = latest.pagesRead ?? 0;
  const pageMoves = (latest.pageMoves ?? []) as any[];
  const pageFindings = latest.pageFindings ?? null;
  const hasPagesSection = v3 && (pageMoves.length > 0 || !!pageFindings);
  const sectionNo = (base: number) => String(hasPagesSection ? base + 1 : base);
  const statementText: Record<string, string> = Object.fromEntries(
    statements.map((s) => [s.code, s.text]),
  );

  // The API decides which pages are gaps (never name the brand, nobody's own
  // site, not a reference page, in the market); older reads fall back to the
  // rule they were served with.
  const neverNamed = ((pageFindings?.silent ?? []) as any[]).filter((p) =>
    p.gap !== undefined ? p.gap : p.named === false && !p.site && p.kind !== 'competitor',
  );

  const jumpToFixes = () => {
    const el = document.getElementById('fix-the-record');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {latest.synthesis?.headline && v3 && (
        <ReadHero
          headline={latest.synthesis.headline}
          summary={latest.synthesis.summary}
          namedFirst={searched?.namedFirstRate ?? null}
          inTheSix={searched?.inclusionRate ?? null}
          meanPosition={searched?.meanPosition ?? null}
          memoryNamedFirst={memoryMode?.namedFirstRate ?? null}
          memoryInTheSix={memoryMode?.inclusionRate ?? null}
          order={heroOrder}
          totalAnswers={gate?.method?.totalAnswers ?? null}
          searchedAnswers={gate?.method?.searchedAnswers ?? searched?.asked ?? null}
          onFixes={(latest.recordFixes ?? []).length > 0 ? jumpToFixes : undefined}
        />
      )}
      {latest.synthesis?.headline && !v3 && (
        <AntCard>
          <Space direction="vertical" size={8}>
            <Text strong style={{ fontSize: 18, lineHeight: 1.35 }}>
              {latest.synthesis.headline}
            </Text>
            {latest.synthesis.summary && (
              <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, maxWidth: '70ch' }}>
                {latest.synthesis.summary}
              </Paragraph>
            )}
          </Space>
        </AntCard>
      )}

      {v3 ? (
        <>
          {/* What you need to know today: the headline above, where the brand
              is missing, and the fixes. The reader who wants nothing else
              stops here. */}
          {neverNamed.length > 0 && <NeverNamedPages pages={neverNamed} brandName={brandName} />}

      <RecordFixes
        fixes={(latest.recordFixes ?? []) as RecordFixEntry[]}
        onToggle={onToggleRecordFix ?? (() => undefined)}
        busyPlayId={busyPlayId}
        canEdit={!readOnly}
        n="1"
      />

          {/* The one survey ask on the page. The read stands alone for the brands
              that never survey; this card is for the ones a belief above will not
              leave alone. Gone the moment a customer line exists. */}
          {!hasSurvey && (
        <AskThemCard
          brandName={brandName}
          models={((latest.panel ?? []) as any[]).map((m) => m.label).filter(Boolean)}
          beliefCount={statements.filter((s) => s.group === 'B').length}
          onSendSurvey={onSendSurvey}
        />
          )}

          {/* All of the data: everything behind the headlines, one click away. */}
          <AllTheData open={allOpen} onToggle={() => setAllOpen((v) => !v)}>
            <Space direction="vertical" size={16} style={{ width: '100%', paddingTop: 16 }}>
        <HowWeAsked
          brandName={brandName}
          market={latest.marketName ?? latest.market ?? null}
          models={((latest.panel ?? []) as any[]).map((m) => m.label).filter(Boolean)}
          statementCount={statements.length}
          competitorCount={((latest.competitors ?? []) as any[]).length}
          perStatement={Number(latest.sampling?.nFloor ?? 8)}
          rank={latest.gate?.method ?? null}
          hasSurvey={hasSurvey}
          pagesMode={latest.searchedSource === 'pages'}
        />

              <GateSummary
                gate={latest.gate}
                note={latest.synthesis?.gate_note}
                panel={latest.panel}
                brandName={brandName}
                n="2"
              />

              {hasPagesSection && (
        <ReadSection
          id="pages"
          n="3"
          title={intl.formatMessage({
            id: 'marketerView.modelBeliefs.pages.sectionTitle',
            defaultMessage: 'What do the models read about you?',
          })}
          sub={intl.formatMessage(
            {
              id: 'marketerView.modelBeliefs.pages.sectionSub',
              defaultMessage:
                'When the models search before answering, they read pages, and those pages shape who they recommend. First, the pages that go with you ranking higher or lower, per assistant. Then what the pages they read most actually say about {brand}, in the pages’ own words.',
            },
            { brand: brandName },
          )}
        >
          <div style={{ display: 'grid', gap: 18 }}>
            {latest.synthesis?.pages_note && (
              <Paragraph style={{ marginBottom: 0, maxWidth: '72ch', fontSize: 14.5, lineHeight: 1.6 }}>
                {latest.synthesis.pages_note}
              </Paragraph>
            )}
            <PagesThatMoveRank pageMoves={pageMoves} brandName={brandName} />
            {pageFindings && (
              <WhatPagesSay findings={pageFindings} statementText={statementText} brandName={brandName} />
            )}
          </div>
        </ReadSection>
              )}

          <FactGrid
            brandName={brandName}
            statements={statements}
            panelCoverage={latest.panelCoverage}
            note={latest.synthesis?.facts_note ?? null}
            pagesMode={pagesMode}
            pagesRead={pagesRead}
            pageReadings={latest.pageReadings ?? {}}
            n={sectionNo(3)}
          />
          <OpinionBeliefs
            brandName={brandName}
            statements={statements}
            hasSurvey={hasSurvey}
            note={latest.synthesis?.beliefs_note ?? null}
            pagesMode={pagesMode}
            pagesRead={pagesRead}
            pageReadings={latest.pageReadings ?? {}}
            n={sectionNo(4)}
          />
          <SurveyOnly notAsked={latest.notAsked ?? []} hasSurvey={hasSurvey} n={sectionNo(5)} />

              <SourcesPanel sources={latest.sources} n={sectionNo(6)} />

      {(history ?? []).length > 0 && (
        <section style={{ paddingTop: 44 }}>
          <Card>
            <Eyebrow style={{ marginBottom: 6 }}>Over time</Eyebrow>
            <div style={{ fontSize: 13, color: THEME.textSecondary, marginBottom: 12 }}>
              One point per read: how often the models listed you among six brands to consider, with web
              search on. The read is repeated every month, so this is where a change shows up.
            </div>
            <ModelBeliefsHistoryChart points={history} customer={v3} />
          </Card>
        </section>
      )}

      {/* The footnote the read cannot do without: what was asked, how many
          times, and what the instrument does NOT measure. */}
      <div style={{ padding: '8px 4px 0' }}>
        <Text style={{ color: THEME.textMuted, fontSize: 12.5, lineHeight: 1.55 }}>
          {v3
            ? `Fine print. The models were asked as a shopper in ${latest.marketName ?? latest.market ?? 'your market'}. Web searches were located in that market for ChatGPT and Claude; Gemini's search takes no location, so for Gemini the market rests on the wording of the question alone. Statements were asked in small mixed batches so no single answer could colour the next. Each answer carries the model's own confidence and a “no belief held” flag, and a statement the models hold nothing about is reported as that rather than averaged into a middle score. There is no single overall score on purpose: the feeling statements, such as loyalty and belonging, are not asked of a model, so a total would be missing half of what Brand Equity is made of.`
            : `Every statement was put to each model ${latest.sampling?.nFloor ?? 8} times in each mode, in small batches drawn from different parts of the survey, with the shopper located in ${latest.market ?? 'your market'}. Answers carry a confidence and a “no belief held” flag, and a statement the models hold nothing about is reported as that rather than averaged into a confident-looking middle. Models compress the scale, so comparisons between them matter more than the absolute numbers.`}
        </Text>
      </div>
            </Space>
          </AllTheData>
        </>
      ) : (
        <>
          <GateSummary
            gate={latest.gate}
            note={latest.synthesis?.gate_note}
            panel={latest.panel}
            brandName={brandName}
          />
          <OverallScores consensus={latest.consensus} byModel={latest.byModel ?? []} />
          <CompetitorScores
            brandName={brandName}
            consensus={latest.consensus}
            competitors={latest.competitors ?? []}
            panelCoverage={latest.panelCoverage}
          />
          <MemoryVsSearch consensus={latest.consensus} memory={latest.memory} />
          <CategoryScores consensus={latest.consensus} />
          <RecordFixes
            fixes={(latest.recordFixes ?? []) as RecordFixEntry[]}
            onToggle={onToggleRecordFix ?? (() => undefined)}
            busyPlayId={busyPlayId}
            canEdit={!readOnly}
          />
          <SourcesPanel sources={latest.sources} />
      {(history ?? []).length > 0 && (
        <section style={{ paddingTop: 44 }}>
          <Card>
            <Eyebrow style={{ marginBottom: 6 }}>Over time</Eyebrow>
            <div style={{ fontSize: 13, color: THEME.textSecondary, marginBottom: 12 }}>
              One point per read: how often the models listed you among six brands to consider, with web
              search on. The read is repeated every month, so this is where a change shows up.
            </div>
            <ModelBeliefsHistoryChart points={history} customer={v3} />
          </Card>
        </section>
      )}
      {/* The footnote the read cannot do without: what was asked, how many
          times, and what the instrument does NOT measure. */}
      <div style={{ padding: '8px 4px 0' }}>
        <Text style={{ color: THEME.textMuted, fontSize: 12.5, lineHeight: 1.55 }}>
          {v3
            ? `Fine print. The models were asked as a shopper in ${latest.marketName ?? latest.market ?? 'your market'}. Web searches were located in that market for ChatGPT and Claude; Gemini's search takes no location, so for Gemini the market rests on the wording of the question alone. Statements were asked in small mixed batches so no single answer could colour the next. Each answer carries the model's own confidence and a “no belief held” flag, and a statement the models hold nothing about is reported as that rather than averaged into a middle score. There is no single overall score on purpose: the feeling statements, such as loyalty and belonging, are not asked of a model, so a total would be missing half of what Brand Equity is made of.`
            : `Every statement was put to each model ${latest.sampling?.nFloor ?? 8} times in each mode, in small batches drawn from different parts of the survey, with the shopper located in ${latest.market ?? 'your market'}. Answers carry a confidence and a “no belief held” flag, and a statement the models hold nothing about is reported as that rather than averaged into a confident-looking middle. Models compress the scale, so comparisons between them matter more than the absolute numbers.`}
        </Text>
      </div>
        </>
      )}
    </Space>
  );
};
